import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { getMissingScopes } from '../../../lib/microsoft/microsoftScopes';

export interface MicrosoftAccountInfo {
  connected: boolean;
  email: string | null;
  user_id: string | null;
  refresh_token: string | null;
  scopes: string[];
  microsoft_sync_activities: boolean;
  microsoft_sync_activities_calendar_id: string;
  microsoft_calendar_ids: string[];
  /** Flag Google sync_activities dello stesso profilo (per la nota "push su entrambi"). */
  google_sync_activities: boolean;
}

export interface MicrosoftCalendar {
  id: string;
  name: string;
  color?: string;
  hexColor?: string;
  isDefault?: boolean;
  canEdit?: boolean;
}

const INITIAL: MicrosoftAccountInfo = {
  connected: false,
  email: null,
  user_id: null,
  refresh_token: null,
  scopes: [],
  microsoft_sync_activities: false,
  microsoft_sync_activities_calendar_id: '',
  microsoft_calendar_ids: [],
  google_sync_activities: false,
};

export function useMicrosoftAccount(userId: string | undefined) {
  const [accountInfo, setAccountInfo] = useState<MicrosoftAccountInfo>(INITIAL);
  const [calendars, setCalendars] = useState<MicrosoftCalendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchingCalendars, setFetchingCalendars] = useState(false);
  // Segna che l'auto-fetch dei calendari è già stato tentato: evita retry infiniti
  // quando la edge microsoft-calendar-list fallisce in modo persistente (calendars resta
  // [] e fetchingCalendars torna false → altrimenti il useEffect ri-triggererebbe all'infinito).
  // Un retry manuale resta possibile chiamando direttamente fetchCalendars.
  const autoFetchAttemptedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(
          'microsoft_account_connected, microsoft_account_email, microsoft_user_id, microsoft_refresh_token, microsoft_scopes, microsoft_sync_activities, microsoft_sync_activities_calendar_id, microsoft_calendar_ids, sync_activities'
        )
        .eq('id', userId)
        .single();

      if (error) throw error;

      const hasTokenAndId = !!(data.microsoft_refresh_token && data.microsoft_user_id);
      setAccountInfo({
        connected: !!data.microsoft_account_connected && hasTokenAndId,
        email: data.microsoft_account_email,
        user_id: data.microsoft_user_id,
        refresh_token: data.microsoft_refresh_token,
        scopes: data.microsoft_scopes || [],
        microsoft_sync_activities: !!data.microsoft_sync_activities,
        microsoft_sync_activities_calendar_id: data.microsoft_sync_activities_calendar_id || '',
        microsoft_calendar_ids: data.microsoft_calendar_ids || [],
        google_sync_activities: !!data.sync_activities,
      });
    } catch (err) {
      console.error('[useMicrosoftAccount] Error fetching account:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // La edge microsoft-calendar-list ricava userId/org dall'Authorization del JWT: body vuoto.
  const fetchCalendars = useCallback(async () => {
    setFetchingCalendars(true);
    try {
      const { data, error } = await supabase.functions.invoke('microsoft-calendar-list', {
        body: {},
      });
      if (error) throw error;
      if (data && Array.isArray(data.calendars)) {
        setCalendars(data.calendars);
      }
    } catch (err) {
      console.error('[useMicrosoftAccount] Error fetching calendars:', err);
    } finally {
      setFetchingCalendars(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (!userId) return false;
    const { error } = await supabase
      .from('user_profiles')
      .update({
        microsoft_account_connected: false,
        microsoft_refresh_token: null,
        microsoft_account_email: null,
        microsoft_user_id: null,
        microsoft_scopes: [],
      })
      .eq('id', userId);

    if (!error) {
      setAccountInfo(INITIAL);
      setCalendars([]);
    }
    return !error;
  }, [userId]);

  const updateUserProfile = useCallback(
    async (updates: Partial<{
      microsoft_sync_activities: boolean;
      microsoft_sync_activities_calendar_id: string;
      microsoft_calendar_ids: string[];
    }>) => {
      if (!userId) return false;
      const { error } = await supabase.from('user_profiles').update(updates).eq('id', userId);
      if (!error) {
        setAccountInfo(prev => ({ ...prev, ...updates }));
      }
      return !error;
    },
    [userId]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Reset del flag auto-fetch quando l'account si disconnette, così una futura
  // riconnessione riabilita un nuovo tentativo automatico.
  useEffect(() => {
    if (!accountInfo.connected) {
      autoFetchAttemptedRef.current = false;
    }
  }, [accountInfo.connected]);

  // Auto-fetch calendari quando connesso e lo scope calendar è presente.
  // Guardia anti-loop: al primo tentativo automatico segniamo autoFetchAttemptedRef;
  // se la edge fallisce (calendars resta []) NON ri-triggeriamo in loop.
  const hasCalendarScope = getMissingScopes(accountInfo.scopes, ['calendar']).length === 0;
  useEffect(() => {
    if (
      accountInfo.connected &&
      hasCalendarScope &&
      calendars.length === 0 &&
      !fetchingCalendars &&
      !autoFetchAttemptedRef.current
    ) {
      autoFetchAttemptedRef.current = true;
      fetchCalendars();
    }
  }, [accountInfo.connected, hasCalendarScope, calendars.length, fetchingCalendars, fetchCalendars]);

  return {
    accountInfo,
    calendars,
    loading,
    fetchingCalendars,
    refresh,
    fetchCalendars,
    disconnect,
    updateUserProfile,
  };
}
